import React, { useCallback, useEffect, useState } from 'react';
import API from '../../../api/API';
import { livelloMassimo } from '../../../hooks/useMe';
import GrigliaPermessi, { chiaveCella } from './GrigliaPermessi';
import { EREDITATO } from './CellaPermesso';

/**
 * La matrice di un principal su tutti gli ambiti in una tabella sola: le risorse in colonna, gli
 * ambiti in riga.
 *
 * Le modifiche non partono una alla volta: si accumulano qui, di qualunque ambito siano, e vanno al
 * server in un solo PUT, che e' tutto o niente. Cosi' dare lo stesso permesso su cinque edizioni e'
 * un salvataggio, e se una delle cinque non e' concedibile non ne passa nessuna.
 */
const TabPermessi = ({ utenti, gruppi, me, ambiti, selezione, onSelezione, onCambiato }) => {
  const [dati, setDati] = useState(null);
  const [modifiche, setModifiche] = useState({});
  const [errori, setErrori] = useState([]);
  const [caricando, setCaricando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const principal = selezione;

  const carica = useCallback(async () => {
    if (!principal) { setDati(null); return; }

    setCaricando(true);
    setErrori([]);
    setModifiche({});

    const res = principal.tipo === 'U'
      ? await API.utenti.permessi(principal.id, null, null, true)
      : await API.gruppiUtenti.permessi(principal.id, null, null, true);

    setDati(res.isSuccess ? res.data : null);
    if (!res.isSuccess) setErrori([res.errorMsg || 'Lettura dei permessi non riuscita']);
    setCaricando(false);
  }, [principal]);

  useEffect(() => { carica(); }, [carica]);

  // Finche' non si e' scelto un principal non c'e' niente da risolvere: senza questa guardia il
  // ramo sui gruppi dereferenziava un principal nullo e la pagina restava bianca.
  const sonoGruppi = principal?.tipo === 'G';
  const amministra = sonoGruppi ? 'GRUPPO_UTENTI' : 'UTENTE';
  const gestibile = principal
    ? ((sonoGruppi ? gruppi : utenti).find(x => x.id === principal.id)?.gestibile ?? false)
    : false;

  /** Si scrive dove si amministra: ambito per ambito, non piu' una volta per tutta la pagina. */
  const puoScrivereSu = (ambito) => gestibile && livelloMassimo(me, ambito, amministra) === 'SCRITTURA';

  const originaleDi = (ambito, risorsa) => {
    const matrice = (dati?.matrici ?? []).find(
      m => m.scopeTipo === ambito.tipo && (m.scopeId ?? null) === (ambito.id ?? null));
    const cella = matrice?.celle?.[risorsa];
    return cella?.esplicito ? cella.livello : EREDITATO;
  };

  const scegli = (ambito, risorsa, valore) => {
    const chiave = chiaveCella(ambito, risorsa);
    const originale = originaleDi(ambito, risorsa);

    setModifiche(prima => {
      const dopo = { ...prima };
      if (valore === originale) delete dopo[chiave];
      else dopo[chiave] = valore;
      return dopo;
    });
  };

  const salva = async () => {
    if (!principal) return;

    setSalvando(true);
    setErrori([]);

    const payload = Object.entries(modifiche).map(([chiave, livello]) => {
      const [ambito, risorsa] = chiave.split('|');
      const [tipo, id] = ambito.split(':');
      return { risorsa, scopeTipo: tipo, scopeId: id === '' ? null : Number(id), livello };
    });

    const res = principal.tipo === 'U'
      ? await API.utenti.scriviPermessi(principal.id, payload)
      : await API.gruppiUtenti.scriviPermessi(principal.id, payload);

    setSalvando(false);

    if (!res.isSuccess) {
      const corpo = res.data;
      setErrori(Array.isArray(corpo) ? corpo : [res.errorMsg || 'Salvataggio non riuscito']);
      return;
    }

    await onCambiato();
    await carica();
  };

  const quanteModifiche = Object.keys(modifiche).length;

  return (
    <>
      <div className="permessi-selettori">
        <label>
          Principal
          <select
            value={principal ? `${principal.tipo}:${principal.id}` : ''}
            onChange={e => {
              if (!e.target.value) { onSelezione(null); return; }
              const [tipo, id] = e.target.value.split(':');
              const nome = tipo === 'U'
                ? utenti.find(u => u.id === Number(id))?.user
                : gruppi.find(g => g.id === Number(id))?.nome;
              onSelezione({ tipo, id: Number(id), nome });
            }}
          >
            <option value="">scegli un utente o un gruppo</option>
            <optgroup label="Utenti">
              {utenti.map(u => <option key={`U${u.id}`} value={`U:${u.id}`}>{u.user}</option>)}
            </optgroup>
            <optgroup label="Gruppi">
              {gruppi.map(g => <option key={`G${g.id}`} value={`G:${g.id}`}>{g.nome}</option>)}
            </optgroup>
          </select>
        </label>
      </div>

      {!principal && (
        <div className="utenti-vuoto">
          <i className="fas fa-info-circle"></i>
          <p>Scegli un utente o un gruppo per vederne la matrice.</p>
        </div>
      )}

      {principal && caricando && (
        <div className="utenti-loading"><div className="loading-spinner"></div><p>Caricamento...</p></div>
      )}

      {principal && !caricando && (
        <>
          {/* La riga compare solo quando ha qualcosa da dire: sopra la griglia lo spazio conta. */}
          {(dati?.bypass || !gestibile) && (
            <div className="permessi-intestazione">
              {dati?.bypass && (
                <span className="utenti-tag utenti-tag-bypass">
                  bypass: qualunque cella qui non conta, ha comunque tutto
                </span>
              )}
              {!gestibile && (
                <span className="utenti-tag">
                  sola lettura: {principal.tipo === 'U' ? 'questo utente ha' : 'questo gruppo ha'} permessi che tu non hai
                </span>
              )}
            </div>
          )}

          <GrigliaPermessi
            ambiti={ambiti}
            matrici={dati?.matrici ?? []}
            celleEsplicite={dati?.celle ?? []}
            me={me}
            modifiche={modifiche}
            puoScrivereSu={puoScrivereSu}
            onScegli={scegli}
          />

          {errori.length > 0 && (
            <ul className="utenti-errore utenti-errore-lista">
              {errori.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}

          <div className="utenti-form-azioni">
            <button className="btn btn-primary" onClick={salva}
                    disabled={quanteModifiche === 0 || salvando}>
              {salvando ? 'Salvataggio...' : `Salva ${quanteModifiche || ''}`.trim()}
            </button>
            <button className="btn btn-secondary" onClick={() => { setModifiche({}); setErrori([]); }}
                    disabled={quanteModifiche === 0 || salvando}>
              Annulla le modifiche
            </button>
            <span className="utenti-nota">
              Il salvataggio e' tutto o niente: se una cella non e' concedibile non viene scritto niente.
            </span>
          </div>
        </>
      )}
    </>
  );
};

export default TabPermessi;
